import React, {Suspense, useEffect, useRef} from 'react';
import aspxPhoto from "../../assets/images/services/pages/customize-software/asp-net.png";
import ServicePage from "../../components/sections/ServicePage";
import Loading from "../../components/common/Loading";
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CustomSoftwareStudio from "../../components/sections/CustomSoftwareStudio";
import {useTranslation} from "react-i18next";
gsap.registerPlugin(ScrollTrigger);

const sectionsOrder = [
  "hero",
  "who",
  "we-build",
  "how-work",
  "tech-stack",
  "industries",
  "process",
  "why-us",
  "engagement",
  "timeline",
  "faq",
  "cta",
];


const CustomizedSoftware = () => {

  const containerRef = useRef(null);
  const {t} = useTranslation()
  const lang = (key) => t(`customizedDevelopmentPage.${key}`);

  useEffect(() => {
    const ctx = gsap.context(() => {
      sectionsOrder.forEach((id, index) => {
        const el = document.getElementById(id);
        if (!el) return;

        gsap.from(el, {
          opacity: 0,
          y: 80,
          duration: 0.8,
          delay: 0.1,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });

        // subtle parallax for each section background
        gsap.to(el, {
          backgroundPositionY: "20%",
          ease: "none",
          scrollTrigger: {
            trigger: el,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      });

      // floating cards in hero
      gsap.to(".hero-float", {
        y: -10,
        repeat: -1,
        yoyo: true,
        duration: 2,
        ease: "sine.inOut",
        stagger: 0.2,
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div>
      <Suspense fallback={<Loading />}>
        <ServicePage serviceKey={"custom"}/>

        <div
          ref={containerRef}
          className="bg-slate-9500 text-slate-900 w-full"
        >

          <CustomSoftwareStudio/>

          {/* WHO WE ARE */}
          <section
            id="who"
            className="py-20 px-4 sm:px-6 lg:px-16 border-t border-slate-900/80 bg-theme-color"
          >
            <div className="max-w-5xl mx-auto grid lg:grid-cols-[1.3fr,1fr] gap-12 items-start">
              <div className="space-y-4">
                <h2 className="text-2xl text-white sm:text-3xl font-semibold">{lang('aStartupTeam')}</h2>
                <p className="text-sm sm:text-base text-slate-300">{lang('weAreASmallProduct')}</p>
                <p className="text-sm sm:text-base text-slate-300">{lang('evenThoughWeAreEarly')}</p>
                <div className="grid sm:grid-cols-3 gap-4 text-xs sm:text-sm">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-slate-400 mb-1">{lang('engagement')}</p>
                    <p className="font-semibold text-slate-50">{lang('fromIdeaSketchToMvp')}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-slate-400 mb-1">{lang('approach')}</p>
                    <p className="font-semibold text-slate-50">{lang('productFirstTransparent')}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-slate-400 mb-1">{lang('scope')}</p>
                    <p className="font-semibold text-slate-50">{lang('weCanAcceptProjects')}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-200 p-5">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">{lang('weAreAGoodFitIf')}</p>
                  <ul className="space-y-2 text-xs sm:text-sm text-slate-300">
                    <li>• {lang('youHaveAnIdea')}</li>
                    <li>• {lang('youWantAPrototype')}</li>
                    <li>• {lang('youNeedAPartner')}</li>
                    <li>• {lang('youValueClarity')}</li>
                  </ul>
                </div>
                <div className="rounded-3xl border border-slate-800 bg-slate-300 p-4 text-[11px] sm:text-xs text-black">
                  <p>{lang('weAreIntentionallyTransparent')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* WHAT WE BUILD */}
          <section
            id="we-build"
            className="py-20 "
          >
            <div className="md:w-theme-container w-mobile-container mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold mb-2">{lang('customSoftwareExactly')}</h2>
                  <p className="text-sm sm:text-base text-slate-500 max-w-2xl">{lang('becauseEveryBusiness')}</p>
                </div>

              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-14">
                {[
                  {
                    title: lang('webApplications'),
                    desc: lang('customDashboards'),
                    tags: ["React", "Next.js", "Tailwind", "TypeScript"],
                  },
                  {
                    title: lang('mobileAndCrossPlatform'),
                    desc: lang('iosAndroidAndCrossPlatform'),
                    tags: ["React Native", "Flutter", "Expo"],
                  },
                  {
                    title: lang('apisAndIntegrations'),
                    desc: lang('secureBackends'),
                    tags: ["ASP.NET", "Python", "PHP", "Node.js", "NestJS", "Go", "PostgreSQL"],
                  },
                ].map((card) => (
                  <div
                    key={card.title}
                    className="rounded-3xl border border-theme-color bg-slate-50 p-5 flex flex-col justify-between"
                  >
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold mb-2">
                        {card.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-700 mb-4">
                        {card.desc}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {card.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] rounded-full border border-slate-700/80 px-2 py-1 bg-theme-color text-slate-200"
                        >
                      {t}
                    </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">{lang('youBringTheIdea')}</h3>
                  <p className="text-sm sm:text-base text-slate-900">{lang('youDoNotNeedAFullSpec')}</p>
                  <ul className="space-y-2 text-xs sm:text-sm text-slate-600">
                    <li>• {lang('discoverySessions')}</li>
                    <li>• {lang('lowFidelityFlows')}</li>
                    <li>• {lang('technicalPlanning')}</li>
                    <li>• {lang('implementationWithFrequentDemos')}</li>
                  </ul>
                </div>
                <div className="relative">
                  <div className="rounded-3xl border border-slate-800 bg-slate-900/60 overflow-hidden">
                    <img
                      src={aspxPhoto}
                      alt="Team collaborating over custom software flows"
                      className="w-full h-full object-cover opacity-80"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* HOW WE WORK */}
          <section
            id="how-work"
            className="py-20 bg-slate-950 border-t border-slate-900"
          >
            <div className="md:w-theme-container w-mobile-container mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <div>
                  <h2 className="text-2xl text-white sm:text-3xl font-semibold mb-2">{lang('aTransparentCollaborative')}</h2>
                  <p className="text-sm sm:text-base text-slate-300 max-w-2xl">{lang('weStructureEveryProject')}</p>
                </div>
              </div>

              <div className="grid lg:grid-cols-[1.4fr,1fr] gap-10">
                <div className="space-y-4">
                  {[
                    {
                      step: "01",
                      title: lang('discoverAndDefine'),
                      desc: lang('weStartWithAWorkshop'),
                    },
                    {
                      step: "02",
                      title: lang('designAndValidate'),
                      desc: lang('weCreateUserFlows'),
                    },
                    {
                      step: "03",
                      title: lang('buildAndIterate'),
                      desc: lang('weBuildInSmallSlices'),
                    },
                    {
                      step: "04",
                      title: lang('launchAndSupport'),
                      desc: lang('weHelpYouDeploy'),
                    },
                  ].map((item) => (
                    <div
                      key={item.step}
                      className="flex gap-4 rounded-3xl border border-slate-800 bg-slate-950/70 p-4"
                    >
                      <div className="flex-shrink-0">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-500/10 border border-indigo-500/40 text-xs font-semibold text-slate-300">
                      {item.step}
                    </span>
                      </div>
                      <div>
                        <h3 className="text-white text-sm sm:text-base font-semibold mb-1">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-400">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-5 space-y-4">
                  <h3 className="text-sm text-white sm:text-base font-semibold">
                    {lang('whatCollaborationLooksLike')}
                  </h3>
                  <ul className="space-y-2 text-xs sm:text-sm text-slate-300">
                    <li>• {lang('weeklyOrBiWeeklyCheckIns')}</li>
                    <li>• {lang('sharedBacklog')}</li>
                    <li>• {lang('directChatChannel')}</li>
                    <li>• {lang('earlyAccessLinks')}</li>
                  </ul>
                  <div className="rounded-2xl border border-slate-700/80 bg-slate-950/70 p-4 text-[11px] text-slate-400">
                    <p>{lang('weDoNotHideBehindJargon')}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* TECH STACK */}
          <section
            id="tech-stack"
            className="py-20 bg-theme-color text-white"
          >
            <div className="md:w-theme-container w-mobile-container mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <div>
                  <h2 className="text-slate-50 text-2xl sm:text-3xl font-semibold mb-2">
                    {lang('modernStackFlexible')}
                  </h2>
                  <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
                    {lang('weChooseTechnologies')}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-6 text-xs sm:text-sm">
                <div className="rounded-3xl bg-slate-100 p-5">
                  <p className="text-slate-600 mb-2">{lang('frontEnd')}</p>
                  <ul className="space-y-1 text-black">
                    <li>{lang('reactNextJs')}</li>
                    <li>{lang('tailwindCss')}</li>
                    <li>{lang('typeScript')}</li>
                    <li>{lang('framerMotionGsap')}</li>
                  </ul>
                </div>
                <div className="rounded-3xl bg-slate-100 p-5">
                  <p className="text-slate-600 mb-2">{lang('backEnd')}</p>
                  <ul className="space-y-1 text-black">
                    <li>{lang('aspxNetNodeNest')}</li>
                    <li>{lang('expressFastify')}</li>
                    <li>{lang('restGraphqlApis')}</li>
                    <li>{lang('authenticationRbac')}</li>
                  </ul>
                </div>
                <div className="rounded-3xl bg-slate-100 p-5">
                  <p className="text-slate-600 mb-2">{lang('dataAndInfra')}</p>
                  <ul className="space-y-1 text-black">
                    <li>{lang('postgresqlMysql')}</li>
                    <li>{lang('mongodbRedis')}</li>
                    <li>{lang('cloudAwsGcpAzure')}</li>
                    <li>{lang('serverlessContainers')}</li>
                  </ul>
                </div>
                <div className="rounded-3xl bg-slate-100 p-5">
                  <p className="text-slate-600 mb-2">{lang('alsoComfortableWith')}</p>
                  <ul className="space-y-1 text-black">
                    <li>{lang('flutterReactNative')}</li>
                    <li>{lang('stripePaymentGateways')}</li>
                    <li>{lang('analyticsTracking')}</li>
                    <li>{lang('thirdPartyIntegrations')}</li>
                  </ul>
                </div>
              </div>

              <div className="mt-10 rounded-3xl bg-slate-100 p-5 text-xs sm:text-sm text-zinc-700">
                <p>
                  {lang('alreadyHaveExistingStack')}
                </p>
              </div>
            </div>
          </section>

          {/* INDUSTRIES & USE CASES */}
          <section
            id="industries"
            className="py-20"
          >
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold mb-2">
                    {lang('weCanWorkInAnyIndustry')}
                  </h2>
                  <p className="text-sm sm:text-base text-slate-600 max-w-2xl">
                    {lang('becauseWeBuildFromFirstPrinciples')}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-10 text-xs sm:text-sm">
                {[
                  {
                    tag: lang('b2bAndSaas'),
                    items: [
                      lang('subscriptionPlatforms'),
                      lang('adminDashboards'),
                      lang('usageAnalytics'),
                      lang('customerPortals'),
                    ],
                  },
                  {
                    tag: lang('operationsAndInternalTools'),
                    items: [
                      lang('inventoryAndLogistics'),
                      lang('approvalWorkflows'),
                      lang('knowledgeBases'),
                      lang('customAutomations'),
                    ],
                  },
                  {
                    tag: lang('customerFacingProducts'),
                    items: [
                      lang('bookingFlows'),
                      lang('onboardingFunnels'),
                      lang('mobileExperiences'),
                      lang('marketingSitesWithCustomLogic'),
                    ],
                  },
                ].map((block) => (
                  <div
                    key={block.tag}
                    className="rounded-3xl bg-theme-color p-5"
                  >
                    <p className="text-slate-400 mb-2">{block.tag}</p>
                    <ul className="space-y-1 text-slate-300">
                      {block.items.map((i) => (
                        <li key={i}>• {i}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-indigo-500/50 bg-indigo-500/5 p-5 text-xs sm:text-sm text-slate-900">
                <p className="font-semibold mb-1">
                  {lang('noPortfolioYet')}
                </p>
                <p>
                  {lang('becauseWeAreStillBuilding')}
                </p>
              </div>
            </div>
          </section>

          {/* PROCESS DEEP DIVE */}
          <section
            id="process"
            className="py-20 bg-gradient-to-b from-slate-950 to-slate-900 border-t border-slate-900"
          >
            <div className="md:w-theme-container w-mobile-container mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold mb-2 text-slate-300">
                    {lang('fromFirstCallToVersion')}
                  </h2>
                  <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
                    {lang('hereIsHowATypicalEngagement')}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto text-xs sm:text-sm">
                <table className="min-w-full border border-slate-800 rounded-2xl overflow-hidden">
                  <thead className="bg-slate-900/80">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-300 border-b border-slate-800">
                      {lang('phase')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-slate-300 border-b border-slate-800">
                      {lang('whatHappens')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-slate-300 border-b border-slate-800">
                      {lang('youGet')}
                    </th>
                  </tr>
                  </thead>
                  <tbody className="bg-slate-950/80 text-slate-300">
                  <tr>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('kickoff')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('weDiscussGoals')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('clearUnderstandingOfScope')}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('productShaping')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('weRefineRequirements')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('aLeanRealisticPlan')}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('design')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('weDesignKeyScreens')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('interactivePrototype')}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('development')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('weImplementFrontEnd')}
                    </td>
                    <td className="px-4 py-3 border-b border-slate-800">
                      {lang('workingProductInStaging')}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">
                      {lang('launchAndAftercare')}
                    </td>
                    <td className="px-4 py-3">
                      {lang('weDeployMonitor')}
                    </td>
                    <td className="px-4 py-3">
                      {lang('stableVersionOne')}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* WHY US */}
          <section
            id="why-us"
            className="py-20 md:w-theme-container w-mobile-container mx-auto text-white"
          >
            <div className="mx-auto grid lg:grid-cols-2 gap-10 items-start text-black">
              <div>
                <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
                  {lang('whyTeamsChoose')}
                </h2>
                <div className="space-y-4 text-xs sm:text-sm text-slate-700">
                  <p>{lang('whenYouWorkWithFounderLed')}</p>
                  <p>{lang('weAreNotTiedToLegacy')}</p>
                </div>
              </div>

              <div className="space-y-4 border border-slate-800 bg-slate-900/70 rounded-3xl">
                <div className="p-5 text-xs sm:text-sm text-slate-300">
                  <p className="font-semibold mb-2">{lang('honestExpectations')}</p>
                  <p>{lang('weAreTransparentAbout')}</p>
                </div>
                <div className="rounded-3xl p-5 text-[11px] sm:text-xs text-slate-400">
                  <p>{lang('ifYourProjectRequires')}</p>
                </div>
              </div>
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-4 text-xs sm:text-sm">
              <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4">
                <p className="font-semibold text-slate-100 mb-1">
                  {lang('weCareAboutProduct')}
                </p>
                <p className="text-slate-300">
                  {lang('weDoNotJustTakeRequirements')}
                </p>
              </div>
              <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4">
                <p className="font-semibold text-slate-100 mb-1">
                  {lang('weAreStackAgnostic')}
                </p>
                <p className="text-slate-300">
                  {lang('weHavePreferences')}
                </p>
              </div>
              <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4">
                <p className="font-semibold text-slate-100 mb-1">
                  {lang('weAreLongTermPartners')}
                </p>
                <p className="text-slate-300">
                  {lang('ourGoalIsToBeTheTeam')}
                </p>
              </div>
            </div>
          </section>

          {/* ENGAGEMENT MODELS */}
          <section
            id="engagement"
            className="py-20 bg-slate-700 text-white"
          >
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold mb-2">
                    {lang('flexibleWaysToWork')}
                  </h2>
                  <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
                    {lang('whetherYouNeedAQuickProof')}
                  </p>
                </div>
                <p className="text-xs sm:text-sm text-slate-400 max-w-xs">
                  {lang('noHiddenFees')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 text-xs sm:text-sm">
                <div className="rounded-3xl bg-theme-color p-5 flex flex-col justify-between">
                  <div>
                    <p className="text-slate-200 mb-1">{lang('forEarlyIdeas')}</p>
                    <h3 className="font-semibold mb-2">{lang('discoveryAndPrototype')}</h3>
                    <p className="text-slate-300 mb-3">
                      {lang('aShortFocusedEngagement')}
                    </p>
                  </div>
                  <p className="text-slate-200">
                    {lang('timelineTwoToFourWeeks')}
                  </p>
                </div>
                <div className="rounded-3xl bg-theme-color p-5 flex flex-col justify-between">
                  <div>
                    <p className="text-slate-200 mb-1">{lang('forVersionOne')}</p>
                    <h3 className="font-semibold mb-2">{lang('mvpBuild')}</h3>
                    <p className="text-slate-300 mb-3">
                      {lang('weDesignBuildAndLaunch')}
                    </p>
                  </div>
                  <p className="text-slate-200">
                    {lang('timelineEightToSixteenWeeks')}
                  </p>
                </div>
                <div className="rounded-3xl bg-theme-color p-5 flex flex-col justify-between">
                  <div>
                    <p className="text-slate-200 mb-1">{lang('forOngoingGrowth')}</p>
                    <h3 className="font-semibold mb-2">{lang('productPartnership')}</h3>
                    <p className="text-slate-300 mb-3">
                      {lang('longTermCollaboration')}
                    </p>
                  </div>
                  <p className="text-slate-200">
                    {lang('timelineMonthlyOrQuarterly')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section
            id="faq"
            className="py-20 text-white bg-gradient-to-b from-slate-950 to-slate-900 border-t border-slate-900"
          >
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-6">
                {lang('questionsYouMightHave')}
              </h2>
              <div className="space-y-4 text-xs sm:text-sm text-slate-300">
                <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4">
                  <p className="font-semibold text-slate-100 mb-1">
                    {lang('doYouOnlyWorkWithCertain')}
                  </p>
                  <p>{lang('noWeCanAcceptProjects')}</p>
                </div>
                <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4">
                  <p className="font-semibold text-slate-100 mb-1">
                    {lang('weDoNotHaveDetailedRequirements')}
                  </p>
                  <p>{lang('notAtAllManyOfOurProjects')}</p>
                </div>
                <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4">
                  <p className="font-semibold text-slate-100 mb-1">
                    {lang('canYouWorkWithOurInHouse')}
                  </p>
                  <p>{lang('yesWeCanActAsExtension')}</p>
                </div>
                <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4">
                  <p className="font-semibold text-slate-100 mb-1">
                    {lang('doYouOfferOngoingMaintenance')}
                  </p>
                  <p>{lang('yesAfterLaunchWeCan')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section
            id="cta"
            className="py-24 px-4 sm:px-6 lg:px-16 text-white bg-slate-950 border-t border-slate-900"
          >
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
                {lang('readyToStartYourCustom')}
              </h2>
              <p className="text-sm sm:text-base text-slate-300 mb-6">
                {lang('tellUsAboutYourIdea')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button className="inline-flex justify-center items-center rounded-lg bg-indigo-500 px-6 py-3 text-sm font-medium text-white shadow-lg shadow-indigo-500/30 hover:bg-indigo-400 transition">
                  {lang('scheduleACall')}
                </button>
                <button className="inline-flex justify-center items-center rounded-lg border border-slate-600 px-6 py-3 text-sm font-medium text-slate-100 hover:border-indigo-400 hover:text-indigo-200 transition">
                  {lang('sendUsYourBrief')}
                </button>
              </div>
              <p className="mt-4 text-xs text-slate-500">
                {lang('evenIfYouAreNotSure')}
              </p>
            </div>
          </section>
        </div>

      </Suspense>
    </div>
  );
};

export default CustomizedSoftware;
